import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowDownToLine, ArrowUpFromLine, ArrowLeftRight, SlidersHorizontal, Image as ImageIcon } from 'lucide-react';
import { inventarioApi } from '@/services/inventarioApi';
import { resolveImageUrl, formatDate } from '@/utils/formatters';
import { InventoryItemPicker } from './InventoryItemPicker';
import type { InventoryItemRefDto, KardexEntryDto, TipoItem, TipoMovimiento } from '@/types/inventario';

const TIPO_ITEM_LABELS: Record<TipoItem, string> = {
  PRODUCTO: 'Gabinete',
  PANTALLA_MODULAR: 'Pantalla Modular',
  CONTROLADOR: 'Controlador',
  FUENTE: 'Fuente',
  TARJETA_RECEPTORA: 'Tarjeta Receptora',
  OTRO: 'Otro',
};

const TIPO_MOV_META: Record<TipoMovimiento, { label: string; icon: typeof ArrowDownToLine; color: string }> = {
  ENTRADA: { label: 'Entrada', icon: ArrowDownToLine, color: 'bg-emerald-100 text-emerald-700' },
  SALIDA: { label: 'Salida', icon: ArrowUpFromLine, color: 'bg-red-100 text-red-700' },
  TRASLADO: { label: 'Traslado', icon: ArrowLeftRight, color: 'bg-blue-100 text-blue-700' },
  AJUSTE: { label: 'Ajuste', icon: SlidersHorizontal, color: 'bg-amber-100 text-amber-700' },
};

export function KardexView() {
  const [item, setItem] = useState<InventoryItemRefDto | null>(null);

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['kardex', item?.id],
    queryFn: () => inventarioApi.kardex(item!.id),
    enabled: !!item,
  });

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Selecciona un ítem</label>
        <InventoryItemPicker value={item} onChange={setItem} placeholder="Buscar por código o nombre…" />
      </div>

      {!item ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-500 text-sm">
          Selecciona un ítem para ver su kardex.
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4">
            {item.imagen ? (
              <img src={resolveImageUrl(item.imagen)!} alt={item.nombre} className="w-14 h-14 object-cover rounded-lg border" />
            ) : (
              <div className="w-14 h-14 bg-gray-100 rounded-lg border flex items-center justify-center"><ImageIcon size={20} className="text-gray-400" /></div>
            )}
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900 truncate">{item.nombre}</h3>
              <p className="text-xs text-gray-500 font-mono">{item.codigo ?? '—'}</p>
              <p className="text-xs text-gray-500">{TIPO_ITEM_LABELS[item.itemType]}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 sm:px-6 py-3 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Historial de movimientos</h3>
            </div>

            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Fecha</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Tipo</th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Cant.</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Origen</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Destino</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Saldo origen</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Saldo destino</th>
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Usuario</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr><td colSpan={8} className="text-center py-8 text-gray-500">Cargando...</td></tr>
                  ) : entries.length === 0 ? (
                    <tr><td colSpan={8} className="text-center py-8 text-gray-500">Sin movimientos</td></tr>
                  ) : (
                    entries.map((e) => <KardexRow key={e.movimientoId} e={e} />)
                  )}
                </tbody>
              </table>
            </div>

            <div className="md:hidden divide-y divide-gray-100">
              {isLoading ? (
                <p className="text-center py-8 text-gray-500">Cargando...</p>
              ) : entries.length === 0 ? (
                <p className="text-center py-8 text-gray-500 text-sm">Sin movimientos</p>
              ) : (
                entries.map((e) => <KardexCard key={e.movimientoId} e={e} />)
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function KardexRow({ e }: { e: KardexEntryDto }) {
  const meta = TIPO_MOV_META[e.tipo];
  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      <td className="px-4 py-2.5 text-sm text-gray-600 whitespace-nowrap">{formatDate(e.fecha)}</td>
      <td className="px-4 py-2.5">
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${meta.color}`}>
          {meta.label}
        </span>
      </td>
      <td className="px-4 py-2.5 text-sm font-semibold text-right">{e.cantidad}</td>
      <td className="px-4 py-2.5 text-sm text-gray-600">{e.almacenOrigen?.nombre ?? '—'}</td>
      <td className="px-4 py-2.5 text-sm text-gray-600">{e.almacenDestino?.nombre ?? '—'}</td>
      <td className="px-4 py-2.5 text-xs text-gray-500">{formatSnapshot(e.stockOrigenAntes, e.stockOrigenDespues)}</td>
      <td className="px-4 py-2.5 text-xs text-gray-500">{formatSnapshot(e.stockDestinoAntes, e.stockDestinoDespues)}</td>
      <td className="px-4 py-2.5 text-xs text-gray-500">{e.usuarioNombre}</td>
    </tr>
  );
}

function KardexCard({ e }: { e: KardexEntryDto }) {
  const meta = TIPO_MOV_META[e.tipo];
  return (
    <div className="px-4 py-3">
      <div className="flex items-center justify-between mb-1">
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${meta.color}`}>{meta.label}</span>
        <span className="text-sm font-semibold">{e.cantidad} ud</span>
      </div>
      <p className="text-xs text-gray-500">
        {e.almacenOrigen?.nombre ?? '—'} → {e.almacenDestino?.nombre ?? '—'}
      </p>
      {(e.stockOrigenDespues != null || e.stockDestinoDespues != null) && (
        <p className="text-xs text-gray-500 mt-0.5">
          {e.stockOrigenDespues != null && <>Saldo origen: {e.stockOrigenAntes} → {e.stockOrigenDespues}</>}
          {e.stockOrigenDespues != null && e.stockDestinoDespues != null && ' · '}
          {e.stockDestinoDespues != null && <>Saldo destino: {e.stockDestinoAntes} → {e.stockDestinoDespues}</>}
        </p>
      )}
      <p className="text-xs text-gray-400 mt-0.5">{formatDate(e.fecha)} · {e.usuarioNombre}</p>
    </div>
  );
}

function formatSnapshot(antes: number | null, despues: number | null): string {
  if (antes == null || despues == null) return '—';
  return `${antes} → ${despues}`;
}
